import { NavLink, Outlet } from 'react-router-dom'

import { ThemeToggle } from '@/components/layout/ThemeToggle'
import { Tooltip } from '@/components/ui/Tooltip'
import { cn } from '@/lib/cn'

const NAV_ITEMS = [
  { to: '/', label: 'Search', end: true },
  { to: '/dashboard', label: 'Dashboard', end: false },
  { to: '/ingest', label: 'Ingest', end: false },
]

const PORTAL_INFO_URL =
  'https://www.njcourts.gov/public/find-a-case/judgment-lien-public-access'

function Logo() {
  return (
    <span
      className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-accent text-white shadow-sm"
      aria-hidden="true"
    >
      <svg viewBox="0 0 20 20" className="h-[18px] w-[18px]" fill="none">
        <path
          d="M10 2.5v15M4.5 6h11M6 6l-2.5 5h5L6 6zM14 6l-2.5 5h5L14 6zM6.5 17.5h7"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    </span>
  )
}

/**
 * The read-only guarantee, where it is actually reassuring.
 *
 * This tool touches a live court system, and the one question anyone asks
 * first is whether it can change anything. That answer belonged in the chrome,
 * not in a footnote below the fold.
 */
function ReadOnlyMark() {
  return (
    <Tooltip content="Every outbound request is checked against a host allow-list. Nothing is ever submitted, filed or purchased.">
      <span className="hidden items-center gap-1.5 rounded-full border border-line bg-surface-sunken px-2 py-0.5 text-2xs font-medium text-ink-muted md:inline-flex">
        <svg viewBox="0 0 12 12" className="h-3 w-3" fill="none" aria-hidden="true">
          <path
            d="M6 1.5l3.5 1.4v3c0 2-1.5 3.7-3.5 4.6-2-.9-3.5-2.6-3.5-4.6v-3L6 1.5z"
            stroke="currentColor"
            strokeWidth="1.1"
            strokeLinejoin="round"
          />
        </svg>
        Read-only
      </span>
    </Tooltip>
  )
}

/** Header, navigation, page frame and the footer note. */
export function AppShell() {
  return (
    <div className="flex min-h-screen flex-col bg-surface">
      <a
        href="#main"
        className="sr-only focus:not-sr-only focus:absolute focus:top-2 focus:left-2 focus:z-(--z-toast) focus:rounded-md focus:bg-accent focus:px-3 focus:py-2 focus:text-sm focus:font-medium focus:text-white"
      >
        Skip to content
      </a>

      <header className="sticky top-0 z-(--z-header) border-b border-line bg-surface/80 backdrop-blur-md">
        <div className="mx-auto flex h-14 max-w-(--container-shell) items-center gap-3 px-4 sm:px-6">
          <NavLink
            to="/"
            className="flex min-w-0 items-center gap-2.5 rounded-md"
            aria-label="NJ Judgment Docket, home"
          >
            <Logo />
            <span className="hidden min-w-0 sm:block">
              <span className="block truncate text-base leading-tight font-semibold tracking-[-0.01em] text-ink">
                NJ Judgment Docket
              </span>
              <span className="block truncate text-2xs leading-tight text-ink-muted">
                Civil Judgment &amp; Order Docket
              </span>
            </span>
          </NavLink>

          <span className="mx-1 hidden h-5 w-px bg-line sm:block" aria-hidden="true" />

          <nav aria-label="Main" className="flex min-w-0 items-center gap-0.5">
            {NAV_ITEMS.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.end}
                className={({ isActive }) =>
                  cn(
                    'rounded-md px-2.5 py-1.5 text-sm font-medium transition-colors duration-100 sm:px-3',
                    isActive
                      ? 'bg-accent-soft text-accent-text'
                      : 'text-ink-secondary hover:bg-surface-hover hover:text-ink',
                  )
                }
              >
                {item.label}
              </NavLink>
            ))}
          </nav>

          <div className="ml-auto flex shrink-0 items-center gap-2">
            <ReadOnlyMark />
            <ThemeToggle />
          </div>
        </div>
      </header>

      <main
        id="main"
        className="mx-auto flex w-full max-w-(--container-shell) flex-1 flex-col gap-5 px-4 py-5 sm:px-6 sm:py-6"
      >
        <Outlet />
      </main>

      <footer className="mt-4 border-t border-line bg-surface-raised">
        <div className="mx-auto flex max-w-(--container-shell) flex-col gap-1.5 px-4 py-5 text-xs text-ink-muted sm:flex-row sm:items-center sm:justify-between sm:px-6">
          <p>
            Read-only. Every request is checked against an allow-list before it is
            sent; nothing is ever submitted, filed or purchased.
          </p>
          <a
            href={PORTAL_INFO_URL}
            target="_blank"
            rel="noreferrer noopener"
            className="inline-flex w-fit items-center gap-1 rounded-sm text-accent-text underline-offset-2 hover:underline"
          >
            NJ Courts · Judgment Lien Public Access
            <svg viewBox="0 0 12 12" className="h-2.5 w-2.5" fill="none" aria-hidden="true">
              <path
                d="M4 2h6v6M10 2L2.5 9.5"
                stroke="currentColor"
                strokeWidth="1.4"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </a>
        </div>
      </footer>
    </div>
  )
}
